/**
 * Display rules for the admin Transactions tab (rows of GET /admin/transactions).
 *
 * Client mirror of the server's types (`enum LedgerEntryType`, then `enum
 * TransactionHistoryType` — the imported rows — in backend/prisma/schema.prisma,
 * served as TX_TYPES by admin-transactions.service.ts)
 * and of its toMajorUnits — the client bundle cannot import server code.
 * transaction-format.test.ts reads the schema and fails when TX_TYPES drifts, so a
 * new ledger type (e.g. imported purchases/payouts) shows up as a red test here
 * until it is added to the dropdown.
 */
import type { AdminTransaction } from '../../game-api';
import { EU_CURRENCIES, OTHER_INVOICE_CURRENCIES } from './invoice-options';

/** Every type the server lists: ledger types, then imported ones, each in the schema's order. */
export const TX_TYPES = [
  'CREDIT',
  'DEBIT',
  'TRANSFER_IN',
  'TRANSFER_OUT',
  'PURCHASE',
  'REWARD',
  'ADMIN_GRANT',
  'ADMIN_REVOKE',
  // Imported real-money rows (`enum TransactionHistoryType`, transaction_history).
  'SHROOM_PURCHASE',
  'SHROOM_PURCHASE_FAILED',
  'WITHDRAWAL_APPROVED',
  'WITHDRAWAL_REJECTED',
] as const;

/** Currencies whose amount is stored in cents (every invoice currency); Shrooms and Coins are whole units. */
const CENT_CURRENCIES: readonly string[] = [...EU_CURRENCIES, ...OTHER_INVOICE_CURRENCIES];

/** "CUR 12.34" in major units, 2 decimals (MAD's amount column). */
export function formatTxAmount(currency: string, delta: number): string {
  const major = CENT_CURRENCIES.includes(currency) ? delta / 100 : delta;
  return `${currency} ${major.toFixed(2)}`;
}

/** E-mail, else username, else user id (MAD's USER column). */
export function txUserLabel(tx: Pick<AdminTransaction, 'userId' | 'user'>): string {
  return tx.user?.email || tx.user?.username || tx.userId || '—';
}

/** CREDIT → "Credit", ADMIN_GRANT → "Admin grant". */
export function txTypeLabel(type: string): string {
  const s = type.toLowerCase().replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}
